import React from 'react';

const LOGO_SRC = `${process.env.PUBLIC_URL}/al-burak-logo.png`;

function formatReceiptDate(date) {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-PK', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatMoney(value) {
  return Number(value || 0).toLocaleString('en-PK', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

function lineProductDiscount(item) {
  return Number(item.discount || 0) * Number(item.quantity || 0);
}

function additionalDiscount(receipt) {
  const items = receipt.items || [];
  const totalDisc = Number(receipt.discount || 0);
  const productDisc = items.reduce((s, i) => s + lineProductDiscount(i), 0);
  return Math.max(0, totalDisc - productDisc);
}

function paidUnitPrice(item, receipt) {
  const qty = Number(item.quantity || 0);
  if (qty <= 0) return 0;
  const gross = qty * Number(item.price || 0);
  const productDisc = lineProductDiscount(item);
  const subtotal = Number(receipt.subtotal || 0);
  const extra = additionalDiscount(receipt);
  const share = subtotal > 0 ? (gross / subtotal) * extra : 0;
  return Math.max(0, gross - productDisc - share) / qty;
}

function returnRefundValue(item, receipt) {
  return Number(item.returnedQty || 0) * paidUnitPrice(item, receipt);
}

function exchangeOldCredit(item, receipt) {
  return Number(item.exchangedQty || 0) * paidUnitPrice(item, receipt);
}

function exchangeNewCharge(item) {
  const rows = Array.isArray(item.exchanges) ? item.exchanges : [];
  return rows.reduce((s, row) => {
    const qty = Number(row.quantity || 0);
    const net = Math.max(0, Number(row.price || 0) - Number(row.discount || 0));
    return s + qty * net;
  }, 0);
}

export default function ShopReceiptPaper({ receipt, shopName, city }) {
  if (!receipt) return null;

  const items = receipt.items || [];
  const returnLines = items.filter((i) => Number(i.returnedQty || 0) > 0);
  const exchangeLines = items.filter((i) => Number(i.exchangedQty || 0) > 0);
  const hasReturns = returnLines.length > 0;
  const hasExchanges = exchangeLines.length > 0;
  const hasAdjustments = hasReturns || hasExchanges;

  const refundTotal = returnLines.reduce((s, i) => s + returnRefundValue(i, receipt), 0);
  const exchangeCredit = exchangeLines.reduce((s, i) => s + exchangeOldCredit(i, receipt), 0);
  const exchangeCharge = exchangeLines.reduce((s, i) => s + exchangeNewCharge(i), 0);
  const exchangeDiff = exchangeCharge - exchangeCredit;
  const net = refundTotal - exchangeDiff;

  return (
    <div className="sp-receipt-paper">
      <div className="sp-receipt-paper__inner">
        <div className="sp-receipt-paper__header">
          <img src={LOGO_SRC} alt="Al-Burak Scents" className="sp-receipt-paper__logo" />
          <h4 className="sp-receipt-paper__brand">AL BURAQ SCENTS</h4>
          <p className="sp-receipt-paper__shop">{shopName}{city ? ` · ${city}` : ''}</p>
          {receipt.voided ? (
            <div className="sp-receipt-paper__void">
              VOID
              <div className="sp-receipt-paper__void-meta">
                {receipt.voidReason ? `Reason: ${receipt.voidReason}` : null}
                {receipt.voidedBy?.name ? ` · By ${receipt.voidedBy.name}` : null}
                {receipt.voidedAt
                  ? ` · ${new Date(receipt.voidedAt).toLocaleString('en-PK')}`
                  : null}
              </div>
            </div>
          ) : null}
        </div>

        <div className="sp-receipt-paper__meta">
          <div>
            <span>Invoice No</span>
            <strong>{receipt.invoiceNumber || '—'}</strong>
          </div>
          <div>
            <span>Time</span>
            <strong>{receipt.time || '—'}</strong>
          </div>
          <div>
            <span>Date</span>
            <strong>{formatReceiptDate(receipt.date)}</strong>
          </div>
          <div>
            <span>Cashier</span>
            <strong>{receipt.cashier || '—'}</strong>
          </div>
        </div>

        {(receipt.customerName || receipt.customerPhone) && (
          <div className="sp-receipt-paper__customer">
            <span>Customer</span>
            <strong>
              {[receipt.customerName, receipt.customerPhone].filter(Boolean).join(' · ') || '—'}
            </strong>
          </div>
        )}

        <table className="sp-receipt-paper__table">
          <thead>
            <tr>
              <th>S.No</th>
              <th>Qty</th>
              <th>Item</th>
              <th>Price</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr key={`${item.id}-${index}`}>
                <td>{index + 1}.</td>
                <td>{item.quantity}</td>
                <td>
                  {item.description}
                  {item.returnedQty || item.exchangedQty ? (
                    <div style={{ fontSize: 10, color: '#666', marginTop: 2 }}>
                      {item.returnedQty ? `Returned: ${item.returnedQty}` : ''}
                      {item.returnedQty && item.exchangedQty ? ' · ' : ''}
                      {item.exchangedQty ? `Exchanged: ${item.exchangedQty}` : ''}
                      {` · Kept: ${Math.max(0, (item.quantity || 0) - (item.returnedQty || 0) - (item.exchangedQty || 0))}`}
                    </div>
                  ) : null}
                </td>
                <td>{formatMoney(item.price)}</td>
                <td>{formatMoney((item.quantity || 0) * (item.price || 0))}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="sp-receipt-paper__totals">
          <div>
            <span>Subtotal</span>
            <strong>Rs {formatMoney(receipt.subtotal)}</strong>
          </div>
          {Number(receipt.discount) > 0 && (
            <div>
              <span>Discount</span>
              <strong>- Rs {formatMoney(receipt.discount)}</strong>
            </div>
          )}
          <div className="sp-receipt-paper__grand">
            <span>Original Total</span>
            <strong>Rs {formatMoney(receipt.total)}</strong>
          </div>
          <div className="sp-receipt-paper__grand">
            <span>Paid</span>
            <strong>Rs {formatMoney(receipt.total)}</strong>
          </div>
        </div>

        {hasReturns ? (
          <div className="sp-receipt-paper__return">
            <h5>Returns</h5>
            {returnLines.map((item, idx) => (
              <p key={`ret-${idx}`}>
                {item.description} — Qty {item.returnedQty} · Paid Rs{' '}
                {formatMoney(returnRefundValue(item, receipt))}
              </p>
            ))}
            <p>
              <strong>Refund due: Rs {formatMoney(refundTotal)}</strong>
            </p>
          </div>
        ) : null}

        {hasExchanges ? (
          <div className="sp-receipt-paper__return">
            <h5>Exchanges</h5>
            {exchangeLines.map((item, idx) => {
              const credit = exchangeOldCredit(item, receipt);
              const charge = exchangeNewCharge(item);
              const rows =
                Array.isArray(item.exchanges) && item.exchanges.length
                  ? item.exchanges
                  : [{ quantity: item.exchangedQty, name: '(not recorded)', price: 0, discount: 0 }];
              return (
                <div key={`ex-${idx}`} style={{ marginBottom: 8 }}>
                  <p>
                    Gave: {item.description} × {item.exchangedQty} · Rs {formatMoney(credit)}
                  </p>
                  {rows.map((row, rIdx) => (
                    <p key={rIdx}>
                      Took: {row.name} × {row.quantity} · Rs{' '}
                      {formatMoney(
                        Number(row.quantity || 0) *
                          Math.max(0, Number(row.price || 0) - Number(row.discount || 0))
                      )}
                    </p>
                  ))}
                  <p>
                    Diff:{' '}
                    {charge - credit > 0
                      ? `Pay Rs ${formatMoney(charge - credit)}`
                      : charge - credit < 0
                        ? `Refund Rs ${formatMoney(credit - charge)}`
                        : 'Even'}
                  </p>
                </div>
              );
            })}
          </div>
        ) : null}

        {hasAdjustments ? (
          <div className="sp-receipt-paper__return">
            <h5>Settlement</h5>
            <p>
              <strong>
                {net > 0
                  ? `Net refund to customer: Rs ${formatMoney(net)}`
                  : net < 0
                    ? `Net payable by customer: Rs ${formatMoney(Math.abs(net))}`
                    : 'Net settled: Rs 0'}
              </strong>
            </p>
          </div>
        ) : null}

        <div className="sp-receipt-paper__footer">
          <p>Thank you for your purchase</p>
          <p>www.alburaqscents.com</p>
        </div>
      </div>
    </div>
  );
}
